// REQ-025 AC8 — what the panel remembers, where it is kept, and why nothing it holds can throw.
//
// **One versioned document per account** in the apps' existing `sl.*` local store, over the same three-call
// adapter `DemoStorage` and `createExpansionStore` already take — so the web's `localStorage`, the phone's
// AsyncStorage and a test's in-memory map all fit as they are, and a preferences-backed adapter is one
// adapter rather than a new model when REQ-003 brings sign-in.
//
//   sl.chartColors.<accountId>
//   { "v": 1, "metric": "costPerResult", "lowColour": null, "highColour": null,
//     "average": { "on": false, "colour": null }, "compareOff": false,
//     "thresholds": { "costPerResult": { "account": { "low": 8.91, "high": 30.44, "suggested": true } } } }
//
// **Three decisions this shape encodes, each from a ruling and not a preference:**
//
//   1. **The thresholds are keyed by metric AND by subject** (D-1147 (h)): a number in a metric's own units
//      means different things for a $150-a-day account and a $9-a-day ad set, so a pair typed against one
//      series may never be silently applied to another. Everything else — the metric, the two colours, the
//      average, the comparison override — is per account, because it is a preference about reading and not a
//      figure about a subject.
//   2. **The resolved pair is stored even when it is the suggested one**, with `suggested: true` beside it.
//      That single flag is what makes REQ-025 AC8's "no flash of the wrong colours" true: the first paint
//      has a pair in hand, so it draws the right colours before AC12's lazy 30-day read has happened, and
//      the read then only REFRESHES the suggestion. Without the flag a restored pair could not be told from
//      a typed one and "Reset to suggested" would have nothing to compare against.
//   3. **A colour is `null` until the reader picks one**, never the resolved token. The default follows the
//      metric's direction (`colorByDefaultEnds`), so a stored `null` keeps following it if the direction
//      table or a token value ever moves, while a stored token would freeze yesterday's default for ever.
//
// `parseColorBySettings` never throws and never returns a partly-built document: a missing value, a value
// from an older build, a value from a NEWER build, a corrupt string and a foreign document all resolve to a
// usable record — because the chart has to open (the same rule `DemoStore` and `parseExpansion` keep).

import { isColorByMetric, type ColorByMetric } from './catalogue';

/** Bumped only when the serialised shape changes in a way `migrateColorBySettings` has to answer for. */
export const COLOR_BY_SETTINGS_VERSION = 1;

export const COLOR_BY_STORAGE_PREFIX = 'sl.chartColors';

/**
 * The subject a threshold pair was typed against: the ACCOUNT on Home, that entity's id on its own page.
 * `'account'` rather than the account id, so the record survives the account id changing spelling and so a
 * demo and a live account under one profile do not share a pair by accident (the KEY already separates them).
 */
export const COLOR_BY_SUBJECT_ACCOUNT = 'account';

export interface ColorByScope {
  accountId: string;
}

/** `sl.chartColors.<accountId>` — per device and per account (REQ-025 AC8: never kept with the account). */
export function colorBySettingsKey(scope: ColorByScope): string {
  return `${COLOR_BY_STORAGE_PREFIX}.${scope.accountId}`;
}

export interface ColorByThresholdRecord {
  readonly low: number;
  readonly high: number;
  /** The pair is the one the 30-day window suggested, not one the reader typed ("Suggested" / "Yours"). */
  readonly suggested: boolean;
}

export interface ColorByAverageSettings {
  /** REQ-025 AC6: off by default — a first visit should need no configuration. */
  readonly on: boolean;
  /** A token from `COLOR_BY_SWATCH_TOKENS`, or `null` for `--viz-average`. */
  readonly colour: string | null;
}

export interface ColorBySettings {
  /** `null` is Static — today's colours, and the default (REQ-025 AC2). */
  readonly metric: ColorByMetric | null;
  /** A token the reader chose for the low end, or `null` to follow the metric's direction. */
  readonly lowColour: string | null;
  readonly highColour: string | null;
  readonly average: ColorByAverageSettings;
  /** REQ-025 AC7: this chart's comparison override, which can only ever turn the comparison OFF. */
  readonly compareOff: boolean;
  /** `[metric][subjectId]` — REQ-025 AC4's last paragraph and D-1147 (h). */
  readonly thresholds: Readonly<Record<string, Readonly<Record<string, ColorByThresholdRecord>>>>;
}

export const COLOR_BY_DEFAULTS: ColorBySettings = Object.freeze({
  metric: null,
  lowColour: null,
  highColour: null,
  average: Object.freeze({ on: false, colour: null }),
  compareOff: false,
  thresholds: Object.freeze({}),
}) as ColorBySettings;

/** A fresh default record — the same values as `COLOR_BY_DEFAULTS`, which is frozen and shared. */
export function colorBySettingsDefaults(): ColorBySettings {
  return COLOR_BY_DEFAULTS;
}

// ---- reading ------------------------------------------------------------------------------------------------

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const str = (value: unknown): string | null =>
  typeof value === 'string' && value !== '' ? value : null;

const bool = (value: unknown, fallback: boolean): boolean =>
  typeof value === 'boolean' ? value : fallback;

const num = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

function thresholdRecordOf(value: unknown): ColorByThresholdRecord | null {
  if (!isRecord(value)) return null;
  const low = num(value.low);
  const high = num(value.high);
  // A stored pair that is not a scale is dropped rather than restored: it could colour nothing, and keeping
  // it would hide the suggestion behind a pair the reader cannot see is broken (REQ-025 AC4).
  if (low === null || high === null || !(high > low)) return null;
  return { low, high, suggested: bool(value.suggested, false) };
}

function thresholdsOf(
  value: unknown,
): Readonly<Record<string, Readonly<Record<string, ColorByThresholdRecord>>>> {
  if (!isRecord(value)) return COLOR_BY_DEFAULTS.thresholds;
  const out: Record<string, Record<string, ColorByThresholdRecord>> = {};
  for (const [metric, bySubject] of Object.entries(value)) {
    // A metric this build does not know is dropped, not kept: the record would be unreachable and would grow
    // for ever. (A metric REMOVED from the catalogue is the only way this happens, and that is a lane's job.)
    if (!isColorByMetric(metric) || !isRecord(bySubject)) continue;
    for (const [subjectId, raw] of Object.entries(bySubject)) {
      const record = thresholdRecordOf(raw);
      if (subjectId === '' || record === null) continue;
      (out[metric] ??= {})[subjectId] = record;
    }
  }
  return out;
}

/**
 * The version step, named so the next shape change has one place to land. Today's rule, both halves of it
 * measured against what a store can really hold:
 *
 *   · `v` **below** `COLOR_BY_SETTINGS_VERSION` (or absent, which is a document written before the field
 *     existed) → recover FIELD BY FIELD, keeping every value this build recognises and defaulting the rest.
 *     An older build that never wrote `compareOff` still restores the metric and the colours, which is the
 *     whole point: a reader does not lose their chart because a field was added.
 *   · `v` **above** it → `null`, and the caller uses the defaults. A newer build's document may spell a
 *     field we recognise with a meaning we do not, and guessing is how a stored `metric` becomes a colour
 *     nobody chose. Nothing is DELETED either — `save` overwrites, and until then the newer build's own
 *     record is still there for it.
 */
export function migrateColorBySettings(doc: unknown): ColorBySettings | null {
  if (!isRecord(doc)) return null;
  const version = num(doc.v) ?? 0;
  if (version > COLOR_BY_SETTINGS_VERSION) return null;
  const metric = doc.metric;
  const average = isRecord(doc.average) ? doc.average : {};
  return {
    metric: isColorByMetric(metric) ? metric : null,
    lowColour: str(doc.lowColour),
    highColour: str(doc.highColour),
    average: { on: bool(average.on, false), colour: str(average.colour) },
    compareOff: bool(doc.compareOff, false),
    thresholds: thresholdsOf(doc.thresholds),
  };
}

/**
 * The remembered settings. **Never throws**: a missing value, a corrupt string, a foreign document, an older
 * build's record and a newer build's record all come back as something the chart can open with.
 */
export function parseColorBySettings(raw: string | null | undefined): ColorBySettings {
  if (typeof raw !== 'string' || raw === '') return COLOR_BY_DEFAULTS;
  let doc: unknown;
  try {
    doc = JSON.parse(raw);
  } catch {
    return COLOR_BY_DEFAULTS;
  }
  return migrateColorBySettings(doc) ?? COLOR_BY_DEFAULTS;
}

export function serializeColorBySettings(settings: ColorBySettings): string {
  return JSON.stringify({ v: COLOR_BY_SETTINGS_VERSION, ...settings });
}

/** Whether anything is remembered at all — a default record is not written, so the key stays absent. */
export function isDefaultColorBySettings(settings: ColorBySettings): boolean {
  return (
    settings.metric === null &&
    settings.lowColour === null &&
    settings.highColour === null &&
    settings.average.on === false &&
    settings.average.colour === null &&
    settings.compareOff === false &&
    Object.keys(settings.thresholds).length === 0
  );
}

// ---- the thresholds, by metric and subject ------------------------------------------------------------------

/** The pair remembered for this metric and this subject, or `null` — then the suggestion (or Static) decides. */
export function thresholdsFor(
  settings: ColorBySettings,
  metric: ColorByMetric,
  subjectId: string,
): ColorByThresholdRecord | null {
  return settings.thresholds[metric]?.[subjectId] ?? null;
}

/** `settings` with one pair remembered. Immutable — every operation returns a new record, as React wants it. */
export function withThresholds(
  settings: ColorBySettings,
  metric: ColorByMetric,
  subjectId: string,
  record: ColorByThresholdRecord,
): ColorBySettings {
  return {
    ...settings,
    thresholds: {
      ...settings.thresholds,
      [metric]: { ...settings.thresholds[metric], [subjectId]: record },
    },
  };
}

/** `settings` with this metric's pair for this subject forgotten ("Reset to suggested"). */
export function withoutThresholds(
  settings: ColorBySettings,
  metric: ColorByMetric,
  subjectId: string,
): ColorBySettings {
  const bySubject = settings.thresholds[metric];
  if (bySubject?.[subjectId] === undefined) return settings;
  const nextForMetric = { ...bySubject };
  delete nextForMetric[subjectId];
  const next = { ...settings.thresholds };
  if (Object.keys(nextForMetric).length === 0) delete next[metric];
  else next[metric] = nextForMetric;
  return { ...settings, thresholds: next };
}

// ---- the store ----------------------------------------------------------------------------------------------

/**
 * The adapter shape — the same three calls as `DemoStorage` (`src/demo/storage.ts`) and `ExpansionStorage`
 * (`src/tree/expansion.ts`), declared here rather than imported so this module depends on neither.
 */
export interface ColorBySettingsStorage {
  get(key: string): Promise<string | null>;
  set(key: string, value: string): Promise<void>;
  remove(key: string): Promise<void>;
}

export interface ColorBySettingsStore {
  readonly key: string;
  /** The remembered settings, or the defaults when nothing is stored, the value is unusable, or storage is not. */
  load(): Promise<ColorBySettings>;
  /** Remember `settings`; a default record removes the key. A refused write is swallowed (private browsing). */
  save(settings: ColorBySettings): Promise<void>;
  /** "Reset to defaults" — and what "Reset demo" / "Start fresh" call (REQ-025 AC8, REQ-001 AC6). */
  clear(): Promise<void>;
}

export function createColorBySettingsStore(
  storage: ColorBySettingsStorage,
  scope: ColorByScope,
): ColorBySettingsStore {
  const key = colorBySettingsKey(scope);
  return {
    key,
    async load() {
      try {
        return parseColorBySettings(await storage.get(key));
      } catch {
        return COLOR_BY_DEFAULTS;
      }
    },
    async save(settings) {
      try {
        if (isDefaultColorBySettings(settings)) await storage.remove(key);
        else await storage.set(key, serializeColorBySettings(settings));
      } catch {
        // storage refused (quota, private browsing): the settings live on in memory and are forgotten on reload
      }
    },
    async clear() {
      try {
        await storage.remove(key);
      } catch {
        // as above
      }
    },
  };
}
